import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  FaPlus, FaMinus, FaWhatsapp, FaPhone, FaEnvelope,
  FaFlask, FaUserMd, FaSyringe, FaUtensils, FaRupeeSign,
  FaShieldAlt, FaArrowRight, FaSearch
} from 'react-icons/fa';

const categories = [
  { id: 'all', label: 'All Questions', icon: null },
  { id: 'general', label: 'About GLP-1', icon: FaFlask },
  { id: 'medical', label: 'Medical & Safety', icon: FaUserMd },
  { id: 'program', label: 'Our Program', icon: FaShieldAlt },
  { id: 'medication', label: 'Medication', icon: FaSyringe },
  { id: 'nutrition', label: 'Food & Lifestyle', icon: FaUtensils },
  { id: 'payment', label: 'Pricing & Payment', icon: FaRupeeSign },
];

const faqs = [
  // ── General / About GLP-1 ───────────────────────────────────────────────
  {
    category: 'general',
    q: 'What is GLP-1 and how does it cause weight loss?',
    a: 'GLP-1 (Glucagon-Like Peptide-1) is a natural hormone your gut releases after eating. GLP-1 medications mimic this hormone at higher levels — they reduce appetite by acting on the brain\'s hunger centre, slow gastric emptying so you feel full longer, and stabilise blood sugar. The result is a significant, sustained reduction in calorie intake without willpower battles.'
  },
  {
    category: 'general',
    q: 'Is GLP-1 treatment approved in India?',
    a: 'Yes. Several GLP-1 medications are approved by the Central Drugs Standard Control Organisation (CDSCO) and are available in India as prescription drugs. Semaglutide (Ozempic) is approved for type 2 diabetes; Liraglutide (Saxenda) is approved for chronic weight management. All medications we use are sourced from licensed Indian pharmacies.'
  },
  {
    category: 'general',
    q: 'How much weight will I actually lose?',
    a: 'Clinical trials show an average of 15–20% body weight loss over 6–12 months. The landmark STEP 1 trial showed 14.9% average loss with semaglutide; SURMOUNT-1 with tirzepatide showed 22.5%. Individual results depend on starting weight, adherence, and metabolism. Our doctors give you honest, personalised expectations during your consultation — not inflated promises.'
  },
  {
    category: 'general',
    q: 'Is GLP-1 safe for South Asians / Indians specifically?',
    a: 'Yes. Studies including Indian-population data show comparable efficacy and safety to global trials. Indians actually benefit more in some ways — GLP-1 medications address visceral fat and insulin resistance, which are disproportionately high in South Asians even at lower BMIs. This is why our eligibility threshold includes BMI ≥ 27 with conditions, lower than the global standard of 30.'
  },
  {
    category: 'general',
    q: 'How is this different from the Ozempic hype on social media?',
    a: 'Social media shows dramatic results, but also unsupervised use and rebound weight gain. What social media misses: these are prescription medications that require medical screening, proper dosing titration, lab monitoring, and lifestyle changes to make results stick. Fitnession provides all of this — medication is the tool, not the whole solution.'
  },

  // ── Medical & Safety ────────────────────────────────────────────────────
  {
    category: 'medical',
    q: 'What are the common side effects?',
    a: 'The most common side effects are nausea (44% of patients), diarrhea (30%), constipation (24%), and vomiting (24%). Importantly, most of these are temporary and peak in the first 4–8 weeks as your body adjusts. We manage them through slow dose titration, dietary adjustments, and anti-nausea support. Serious side effects are rare but monitored by your doctor.'
  },
  {
    category: 'medical',
    q: 'Are there any serious risks I should know about?',
    a: 'GLP-1 medications carry a boxed warning for thyroid C-cell tumours (seen in rodents; rare risk in humans) and require screening for personal/family history. Pancreatitis is rare (<0.5%). Gallbladder issues occur in 1–2% of patients. These are exactly the reasons medical supervision is non-negotiable — our doctors screen for all contraindications before prescribing.'
  },
  {
    category: 'medical',
    q: 'Who should NOT take GLP-1 medication?',
    a: 'GLP-1 medications are not suitable if you are: pregnant or breastfeeding, have a personal or family history of medullary thyroid cancer or MEN2 syndrome, have Type 1 diabetes, have a history of pancreatitis, have severe kidney or liver disease, or have an active eating disorder. Our eligibility assessment screens for all of these.'
  },
  {
    category: 'medical',
    q: 'I have PCOD / hypothyroid. Can I still do this?',
    a: 'In most cases, yes — and GLP-1 may be particularly beneficial for you. PCOD and insulin resistance are closely linked; GLP-1 improves insulin sensitivity which can help regulate hormones. Hypothyroid patients can participate provided thyroid function is adequately managed. Your doctor will review your current medications and thyroid levels before prescribing.'
  },
  {
    category: 'medical',
    q: 'I have Type 2 diabetes and am on medication. Is this safe?',
    a: 'GLP-1 medications were originally developed for Type 2 diabetes (Ozempic is an approved diabetes medication). However, combining them with certain diabetes medications (especially insulin or sulfonylureas) requires careful dose adjustment to prevent hypoglycaemia. Your doctor will review your full medication list and adjust as needed — this is a core part of our program.'
  },
  {
    category: 'medical',
    q: 'What lab tests are done and why?',
    a: 'Before starting: HbA1c, fasting glucose, lipid panel, liver function (LFT), kidney function (creatinine, eGFR), thyroid (TSH), and a complete blood count. Monthly during the program: HbA1c, lipids, LFT, kidney function. This baseline and monitoring protects your safety and helps your doctor adjust your treatment plan as your body responds.'
  },

  // ── Our Program ─────────────────────────────────────────────────────────
  {
    category: 'program',
    q: 'What exactly is included in the ₹24,999/month?',
    a: 'Everything: GLP-1 medication delivered to your door, bi-weekly doctor consultations, weekly nutrition coaching, monthly comprehensive blood work, 24/7 WhatsApp support, mobile app access, and community membership. There are no hidden add-ons. The consultation fee (₹1,000) is refunded if you enrol.'
  },
  {
    category: 'program',
    q: 'How does the doctor consultation work?',
    a: 'It is a 30-minute video call with one of our board-certified doctors. They review your health history, medications, goals, and lab results, and discuss whether GLP-1 is appropriate for you. If eligible, they issue a prescription. The consultation costs ₹1,000 and is fully refunded upon programme enrolment.'
  },
  {
    category: 'program',
    q: 'What happens after I enrol?',
    a: 'Week 1: Your medication is shipped to your home (temperature-controlled), you receive an injection training video, and you have an onboarding call with your nutrition coach. Week 2 onwards: Weekly coaching check-ins, app tracking, WhatsApp support. Month 2: First bi-weekly doctor consultation. Month 3: Comprehensive lab review and plan adjustment.'
  },
  {
    category: 'program',
    q: 'Can I cancel anytime?',
    a: 'Yes, after completing a minimum of 3 months (our clinical minimum for meaningful results). We ask for 30 days notice before the next billing cycle. The 6-month prepay plan is non-refundable after month 1, but can be paused in exceptional medical circumstances.'
  },
  {
    category: 'program',
    q: 'What if the medication does not work for me?',
    a: 'After 3 months of full adherence, if you have not lost at least 5% of starting body weight, your doctor will reassess. This may mean adjusting dosage, switching medication (e.g., from liraglutide to semaglutide), or investigating underlying metabolic issues. With proper supervision, this outcome is rare.'
  },
  {
    category: 'program',
    q: 'How is Fitnession different from just getting Ozempic from a clinic?',
    a: 'A standalone clinic prescription gives you medication — nothing else. Fitnession adds: weekly nutrition coaching (Indian food specific), lifestyle habit building, monthly labs + doctor review, 24/7 WhatsApp support, and an app for tracking. Research consistently shows medication + lifestyle intervention produces 2× better long-term results than medication alone.'
  },

  // ── Medication ──────────────────────────────────────────────────────────
  {
    category: 'medication',
    q: 'Which medication will I be prescribed?',
    a: 'Your doctor selects the right medication based on your health profile, existing conditions, and goals. Options include Semaglutide (Ozempic — weekly injection), Liraglutide (Saxenda — daily injection), and Tirzepatide (Mounjaro — weekly, dual GLP-1/GIP action, strongest results). The doctor decides; you do not choose.'
  },
  {
    category: 'medication',
    q: 'Do I have to inject myself? Is it painful?',
    a: "Yes, but it's far simpler than it sounds. The pen is pre-filled with a tiny (4mm) needle — thinner than a hair. Most patients describe it as a slight pinch at most. We provide a step-by-step injection training video, and our team is on WhatsApp if you have questions. The vast majority of patients are comfortable after their first or second dose."
  },
  {
    category: 'medication',
    q: 'How is the medication stored?',
    a: 'Unopened pens are stored in the refrigerator (2–8°C). After first use, they can be kept at room temperature (below 30°C) for up to 28–56 days (depending on the medication). We ship in temperature-controlled packaging and provide detailed storage instructions. Do not freeze. Do not leave in a hot car.'
  },
  {
    category: 'medication',
    q: 'Is the medication authentic? Where is it sourced from?',
    a: 'We partner exclusively with licensed Indian pharmacies and authorised distributors. All medication comes in tamper-proof, manufacturer-sealed packaging with batch numbers and expiry dates you can verify. We never source medication from grey markets or unofficial channels.'
  },
  {
    category: 'medication',
    q: 'What happens when I stop medication?',
    a: 'Some weight regain is common if medication is stopped abruptly without sustained lifestyle changes — this is well-documented globally ("Ozempic rebound"). This is why our program invests heavily in habit coaching alongside medication. Patients who maintain dietary changes typically retain 60–70% of their weight loss. Your doctor will plan a structured tapering strategy for you.'
  },

  // ── Nutrition & Lifestyle ───────────────────────────────────────────────
  {
    category: 'nutrition',
    q: 'Can I eat Indian food — roti, rice, dal, sabzi?',
    a: "Absolutely — that's our speciality. We do not give you a western diet template. Your nutritionist creates personalised Indian meal plans that work with your preferences, family cooking style, regional cuisine, and schedule. You will learn smart portioning of Indian staples, not elimination."
  },
  {
    category: 'nutrition',
    q: 'Can I eat normally at family functions, weddings, and festivals?',
    a: 'Yes. Our coaches prepare you with practical guides for eating at events — what to choose, how much, and how to get back on track. Flexibility is built into the plan. Missing one biryani at a wedding will not derail 6 months of progress.'
  },
  {
    category: 'nutrition',
    q: 'Do I need to exercise?',
    a: 'Exercise is part of the programme but the intensity is customised to your current fitness level. Many of our patients start with walking and yoga — no gym required. As you lose weight and energy increases, your coach progressively adjusts. Strength training is strongly encouraged from month 2–3 to preserve muscle mass during weight loss.'
  },
  {
    category: 'nutrition',
    q: 'I have a busy work schedule. How much time will this take?',
    a: 'The weekly coaching call is 30 minutes (video or audio). Doctor check-ins are 20 minutes bi-weekly. Daily app logging takes under 5 minutes. Our meal plans are designed for working professionals — batch cooking guides, quick meal options, and restaurant eating strategies are included.'
  },

  // ── Payment & Logistics ─────────────────────────────────────────────────
  {
    category: 'payment',
    q: 'What are the payment options?',
    a: 'Monthly billing (₹24,999/month, cancel after 3 months with 30 days notice), 6-month prepay (₹1,35,000 — saves ₹15,000), and EMI via Bajaj Finserv, ZestMoney, and major credit cards. We accept UPI, net banking, and all major debit/credit cards. GST invoices provided.'
  },
  {
    category: 'payment',
    q: 'Will insurance cover this?',
    a: 'Most Indian health insurance policies do not currently cover weight-loss medication. Some corporate group health policies may partially reimburse — we provide detailed GST invoices for reimbursement claims. We recommend checking your policy\'s "obesity treatment" or "endocrinology" coverage with your HR team.'
  },
  {
    category: 'payment',
    q: 'Is the ₹1,000 consultation fee refundable?',
    a: 'Yes — fully. If you complete the consultation and enrol in the programme within 7 days, ₹1,000 is credited against your first month. If you are found ineligible by the doctor, the ₹1,000 is refunded within 5 business days.'
  },
  {
    category: 'payment',
    q: 'Where is the medication delivered? Is it available in my city?',
    a: 'We currently deliver across major Indian cities including Mumbai, Delhi NCR, Bangalore, Hyderabad, Chennai, Pune, Kolkata, Ahmedabad, and more. Delivery takes 2–4 business days. For cities not yet listed, contact us — we can often arrange via licensed courier partnerships.'
  },
];

const GLP1FAQ = () => {
  const [activeCategory, setActiveCategory] = useState('all');
  const [openIndex, setOpenIndex] = useState(null);
  const [search, setSearch] = useState('');

  const filtered = faqs.filter(f => {
    const matchesCategory = activeCategory === 'all' || f.category === activeCategory;
    const matchesSearch = search.trim() === '' ||
      f.q.toLowerCase().includes(search.toLowerCase()) ||
      f.a.toLowerCase().includes(search.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const toggle = (i) => setOpenIndex(openIndex === i ? null : i);

  return (
    <div className="bg-white">

      {/* ── Hero ─────────────────────────────────────────────────────── */}
      <section className="bg-gradient-to-br from-navy-900 via-navy-800 to-navy-900 py-16 text-white text-center relative overflow-hidden">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)', backgroundSize: '40px 40px' }} />
        </div>
        <div className="relative max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="inline-flex items-center bg-gold-500/20 border border-gold-500/40 rounded-full px-4 py-1.5 mb-6">
            <span className="w-2 h-2 rounded-full bg-gold-400 mr-2 animate-pulse" />
            <span className="text-gold-400 text-sm font-semibold uppercase tracking-wider">Frequently Asked Questions</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            Your Questions,
            <span className="block text-gold-400">Answered Honestly</span>
          </h1>
          <p className="text-gray-300 text-lg mb-8">
            {faqs.length} questions across medical safety, medication, nutrition, pricing and more.
            If you don't find your answer, we're a WhatsApp message away.
          </p>

          {/* Search bar */}
          <div className="relative max-w-xl mx-auto">
            <FaSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search questions..."
              value={search}
              onChange={e => { setSearch(e.target.value); setOpenIndex(null); }}
              className="w-full pl-10 pr-4 py-3.5 rounded-xl bg-white/10 border border-white/20 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-gold-400 focus:border-transparent"
            />
          </div>
        </div>
      </section>

      {/* ── Category Filter ───────────────────────────────────────────── */}
      <div className="sticky top-16 z-30 bg-white border-b border-gray-100 shadow-sm">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex gap-2 overflow-x-auto py-3 scrollbar-hide">
            {categories.map(cat => (
              <button
                key={cat.id}
                onClick={() => { setActiveCategory(cat.id); setOpenIndex(null); }}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold whitespace-nowrap transition-all ${
                  activeCategory === cat.id
                    ? 'bg-navy-900 text-white shadow-md'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {cat.icon && <cat.icon className={`text-xs ${activeCategory === cat.id ? 'text-gold-400' : ''}`} />}
                {cat.label}
                {cat.id !== 'all' && (
                  <span className={`text-xs px-1.5 py-0.5 rounded-full ml-0.5 ${
                    activeCategory === cat.id ? 'bg-white/20 text-white' : 'bg-gray-200 text-gray-500'
                  }`}>
                    {faqs.filter(f => f.category === cat.id).length}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* ── FAQ Accordion ─────────────────────────────────────────────── */}
      <section className="py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">

          {filtered.length === 0 ? (
            <div className="text-center py-16">
              <p className="text-4xl mb-4">🔍</p>
              <p className="text-xl font-semibold text-gray-700 mb-2">No results found</p>
              <p className="text-gray-500 mb-6">Try a different search term or browse by category.</p>
              <button onClick={() => { setSearch(''); setActiveCategory('all'); }} className="text-gold-600 font-semibold hover:underline">
                Clear filters
              </button>
            </div>
          ) : (
            <>
              {/* Group by category when showing all */}
              {activeCategory === 'all' && search.trim() === '' ? (
                categories.filter(c => c.id !== 'all').map(cat => {
                  const catFaqs = faqs.filter(f => f.category === cat.id);
                  if (catFaqs.length === 0) return null;
                  const globalStart = faqs.indexOf(catFaqs[0]);
                  return (
                    <div key={cat.id} className="mb-10">
                      <div className="flex items-center gap-2 mb-4">
                        {cat.icon && <cat.icon className="text-gold-500 text-lg" />}
                        <h2 className="text-lg font-bold text-navy-900">{cat.label}</h2>
                        <div className="flex-1 h-px bg-gray-100 ml-2" />
                      </div>
                      <div className="space-y-2">
                        {catFaqs.map((faq, localIdx) => {
                          const idx = globalStart + localIdx;
                          return <FAQItem key={idx} faq={faq} idx={idx} openIndex={openIndex} toggle={toggle} />;
                        })}
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="space-y-2">
                  {search.trim() !== '' && (
                    <p className="text-sm text-gray-500 mb-4">{filtered.length} result{filtered.length !== 1 ? 's' : ''} for "{search}"</p>
                  )}
                  {filtered.map((faq, i) => (
                    <FAQItem key={i} faq={faq} idx={i} openIndex={openIndex} toggle={toggle} />
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </section>

      {/* ── Still Have Questions ──────────────────────────────────────── */}
      <section className="py-12 bg-gray-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-3">Still Have Questions?</h2>
          <p className="text-gray-500 mb-8">
            Our medical team is available via WhatsApp and phone. We typically respond within 2 hours.
          </p>
          <div className="grid sm:grid-cols-3 gap-4 mb-8">
            <a
              href="https://wa.me/919579520586"
              className="flex flex-col items-center bg-white border border-gray-200 rounded-2xl p-5 hover:shadow-md transition-shadow group"
            >
              <div className="w-12 h-12 bg-green-50 rounded-full flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
                <FaWhatsapp className="text-green-500 text-2xl" />
              </div>
              <p className="font-semibold text-gray-900 text-sm">WhatsApp</p>
              <p className="text-xs text-gray-500 mt-0.5">+91 9579520586</p>
              <p className="text-xs text-green-600 mt-1 font-medium">Fastest response</p>
            </a>
            <a
              href="tel:+919579520586"
              className="flex flex-col items-center bg-white border border-gray-200 rounded-2xl p-5 hover:shadow-md transition-shadow group"
            >
              <div className="w-12 h-12 bg-blue-50 rounded-full flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
                <FaPhone className="text-blue-500 text-xl" />
              </div>
              <p className="font-semibold text-gray-900 text-sm">Phone</p>
              <p className="text-xs text-gray-500 mt-0.5">+91 9579520586</p>
              <p className="text-xs text-blue-600 mt-1 font-medium">Mon–Sat, 9am–7pm</p>
            </a>
            <a
              href="mailto:glp1@fitnession.fit"
              className="flex flex-col items-center bg-white border border-gray-200 rounded-2xl p-5 hover:shadow-md transition-shadow group"
            >
              <div className="w-12 h-12 bg-purple-50 rounded-full flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
                <FaEnvelope className="text-purple-500 text-xl" />
              </div>
              <p className="font-semibold text-gray-900 text-sm">Email</p>
              <p className="text-xs text-gray-500 mt-0.5">glp1@fitnession.fit</p>
              <p className="text-xs text-purple-600 mt-1 font-medium">Reply within 4 hours</p>
            </a>
          </div>

          <div className="bg-gradient-to-r from-navy-900 to-navy-800 rounded-2xl p-6 text-white">
            <p className="font-bold text-lg mb-2">Ready to Start?</p>
            <p className="text-gray-300 text-sm mb-4">Free eligibility check — takes 5 minutes. No commitment required.</p>
            <Link
              to="/eligibility"
              className="inline-flex items-center bg-gold-500 hover:bg-gold-600 text-white px-8 py-3 rounded-xl font-bold transition-all shadow-lg"
            >
              Check Your Eligibility <FaArrowRight className="ml-2" />
            </Link>
          </div>
        </div>
      </section>

    </div>
  );
};

// ─── FAQ Item sub-component ───────────────────────────────────────────────────
const FAQItem = ({ faq, idx, openIndex, toggle }) => {
  const isOpen = openIndex === idx;
  return (
    <div className={`border rounded-xl overflow-hidden transition-all ${isOpen ? 'border-gold-300 shadow-md' : 'border-gray-200 hover:border-gray-300'}`}>
      <button
        onClick={() => toggle(idx)}
        className="w-full flex items-start justify-between p-5 text-left"
      >
        <span className={`font-semibold pr-4 leading-snug text-sm sm:text-base ${isOpen ? 'text-navy-900' : 'text-gray-800'}`}>
          {faq.q}
        </span>
        <span className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center mt-0.5 ${isOpen ? 'bg-gold-500 text-white' : 'bg-gray-100 text-gray-500'}`}>
          {isOpen ? <FaMinus className="text-xs" /> : <FaPlus className="text-xs" />}
        </span>
      </button>
      {isOpen && (
        <div className="px-5 pb-5 border-t border-gold-100">
          <p className="text-gray-600 leading-relaxed text-sm pt-4">{faq.a}</p>
        </div>
      )}
    </div>
  );
};

export default GLP1FAQ;
